/**
 * mastery.ts — Ore Mastery derivation. Pure reads off stats.oreMined; the bonus
 * feeds the shared visitSources in modifiers.ts (so it shows in both the live
 * multipliers and the breakdown).
 */
import type { GameState } from '../core/GameState';
import { MASTERY, MAX_MASTERY_TIER } from '../data';

/** How many mastery tiers an ore has reached (0..MAX_MASTERY_TIER). */
export function oreMasteryTier(state: GameState, oreId: string): number {
  const mined = state.stats.oreMined[oreId] ?? 0;
  let tier = 0;
  for (const t of MASTERY.thresholds) {
    if (mined >= t) tier++;
    else break;
  }
  return tier;
}

/** Mining multiplier from one ore's mastery: (1 + bonus)^tier. */
export function oreMasteryMult(state: GameState, oreId: string): number {
  return Math.pow(1 + MASTERY.bonusPerTier, oreMasteryTier(state, oreId));
}

/** Cumulative-mined needed for the next tier, or null if maxed. */
export function nextMasteryThreshold(state: GameState, oreId: string): number | null {
  const tier = oreMasteryTier(state, oreId);
  if (tier >= MAX_MASTERY_TIER) return null;
  return MASTERY.thresholds[tier] ?? null;
}

/** Mastery is surfaced once the player has mined enough of anything. */
export function masteryUnlocked(state: GameState): boolean {
  return state.stats.totalOreMined >= MASTERY.thresholds[0]!;
}
